import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { logError } from "@/lib/logger";

/**
 * Authentication context type definition
 * @interface AuthContextType
 */
interface AuthContextType {
  /** Current authenticated user */
  user: User | null;
  /** Current session */
  session: Session | null;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  /** Sign up with email, password and optional metadata */
  signUp: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<{ error: Error | null; user: User | null }>;
  /** Sign out the current user */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication provider component
 * Manages user authentication state and provides auth methods
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @returns {JSX.Element} The AuthProvider component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    // Wrap everything in try-catch to prevent crashes
    const initAuth = async () => {
      try {
        // Check if supabase client is available
        if (!supabase || !supabase.auth) {
          console.warn('Supabase not initialized, running in offline mode');
          if (mounted) {
            setIsLoading(false);
          }
          return;
        }
        
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('Auth session error:', error);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (_event, session) => {
            if (!mounted) return;
            setSession(session);
            setUser(session?.user ?? null);
            setIsLoading(false);
          }
        );

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        if (!mounted) return;
        console.error('Auth initialization error:', err);
        // Don't crash - just set loading to false
        setIsLoading(false);
      }
    };
    
    initAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      if (!email || !password) {
        return { error: new Error('Email and password are required') };
      }
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (err) {
      logError('Sign in error', err instanceof Error ? err : new Error(String(err)));
      return { error: err instanceof Error ? err : new Error('An unexpected error occurred during sign in') };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata?: Record<string, unknown>) => {
    try {
      if (!email || !password) {
        return { error: new Error('Email and password are required'), user: null };
      }
      if (password.length < 8) {
        return { error: new Error('Password must be at least 8 characters'), user: null };
      }
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });
      return { error, user: data.user };
    } catch (err) {
      logError('Sign up error', err instanceof Error ? err : new Error(String(err)));
      return { error: err instanceof Error ? err : new Error('An unexpected error occurred during sign up'), user: null };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      logError('Sign out error', err instanceof Error ? err : new Error(String(err)));
      // Even if the server-side sign out fails, clear the local state
      setUser(null);
      setSession(null);
    }
  }, []);

  // Render children even if auth failed, but provide null user
  // This prevents the app from crashing completely
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access authentication context
 * @returns {AuthContextType} The authentication context
 * @throws {Error} If used outside of AuthProvider
 * @example
 * const { user, signIn, signOut } = useAuth();
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
